import * as Sentry from '@sentry/react';
import { createContext, Suspense, useContext, useEffect, useMemo } from 'react';

import {
  createBrowserRouter,
  createRoutesFromElements,
  LoaderFunctionArgs,
  Navigate,
  redirect,
  Route,
  RouterProvider,
  useLoaderData,
  useLocation,
  useRevalidator,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SWRConfig } from 'swr';
import { Provider } from 'urql';
import { fetcher } from '~/helpers/fetch';
import { getRequestHash } from '~/helpers/requestHash';
import { validateRedirect } from '~/helpers/redirect';
import { DefaultLayout } from '~/layouts/DefaultLayout';
import { NotFound } from '~/routes/NotFound';
import { User } from '~/types';
import './App.css';
import { client } from './client';
import { UppyInstanceManager } from './components/upload2';

// Account
import * as accounts_new from './routes/accounts.new';

// Activity
import { markAsRead } from './components/activity/ActivityListItem';
import { markAllAsRead } from './components/activity/MarkAllAsReadButton';
import * as activity from './routes/activity';

// Inbox

// Jobs
import { JobHistory } from '~/components/job/JobView/JobHistory';
import * as jobs from '~/routes/jobs';
import * as jobs_$view_$jobId from '~/routes/jobs.$view.$jobId';
import * as jobs_$view_$jobId_edit from '~/routes/jobs.$view.$jobId.edit';
import * as jobs_$view_calendar_$eventId from '~/routes/jobs.$view.calendar.$eventId';
import * as jobs_$view_new from '~/routes/jobs.($view).new';
import { DayView } from './components/calendar/DayView';
import * as automations_$automationId from './routes/automations.$automationId';
import * as automations_$automationId_edit from './routes/automations.$automationId.edit';
import * as automations_$automationId_tasks_$taskId from './routes/automations.$automationId.tasks.$taskId';
import * as automations__index from './routes/automations._index';
import * as automations_new from './routes/automations.new';
import * as bookings_$bookingId from './routes/bookings.$bookingId';
import * as calendar__index from './routes/calendar._index';
import * as calendar_monthly_$placeId from './routes/calendar.monthly.$placeId';
import * as jobs_$view from './routes/jobs.$view';
import * as jobs_$view_$jobId_tasks_$taskId from './routes/jobs.$view.$jobId.tasks.$taskId';
import * as jobs__index from './routes/jobs._index';

// Job Templates
import * as templates_$templateId from '~/routes/job-templates.$templateId';
import * as templates_$templateId_clone from '~/routes/job-templates.$templateId.clone';
import * as templates_$templateId_edit from '~/routes/job-templates.$templateId.edit';
import { TemplateTaskView } from '~/routes/job-templates.$templateId.tasks.$taskId';
import * as templates__index from '~/routes/job-templates._index';
import * as templates_new from '~/routes/job-templates.new';

//Sites
import * as sites from '~/routes/sites';
import * as sites_$siteId from '~/routes/sites.$siteId';
import * as sites_$siteId_assets_$assetId from '~/routes/sites.$siteId.assets.$assetId';
import * as sites_$siteId_assets_$assetId_deploy from '~/routes/sites.$siteId.assets.$assetId.deploy';
import * as sites_$siteId_assets_$assetId_edit from '~/routes/sites.$siteId.assets.$assetId.edit';
import * as sites_$siteId_assets_$assetId_jobs_$jobId from '~/routes/sites.$siteId.assets.$assetId.jobs.$jobId';
import * as sites_$siteId_assets_$assetId_jobs_$jobId_edit from '~/routes/sites.$siteId.assets.$assetId.jobs.$jobId.edit';
import * as sites_$siteId_assets_$assetId_jobs_$jobId_tasks_$taskId from '~/routes/sites.$siteId.assets.$assetId.jobs.$jobId.tasks.$taskId';
import * as sites_$siteId_assets_$assetId_jobs_new from '~/routes/sites.$siteId.assets.$assetId.jobs.new';
import * as sites_$siteId_assets_new from '~/routes/sites.$siteId.assets.new';
import * as sites_$siteId_edit from '~/routes/sites.$siteId.edit';
import * as sites_$siteId_spaces_$spaceId from '~/routes/sites.$siteId.spaces.$spaceId';
import * as sites_$siteId_spaces_$spaceId_edit from '~/routes/sites.$siteId.spaces.$spaceId.edit';
import * as sites_$siteId_spaces_new from '~/routes/sites.$siteId.spaces.new';
import * as sites_$siteId_stocktake from '~/routes/sites.$siteId.stocktake';
import * as sites_new from '~/routes/sites.new';

// Stock
import { Items } from '~/routes/items';
import { StockMovements } from '~/routes/stock-movements';
import { StockOnHand } from '~/routes/stock-on-hand';
import { StockMovementRoute } from './routes/stock-movements/[id]';

// Assets
import { AssetConfig as Config } from '~/routes/asset-config';
import { AssetModels } from '~/routes/asset-models/index';
import { Assets } from '~/routes/assets';
import { Deployments } from '~/routes/deployments';
import { DeploymentView } from '~/routes/deployments.$id';
import { EditDeploymentForm } from '~/routes/deployments.$id.edit';
import { AssetForm } from './components/assets/AssetForm';

// More
import { Ability, RawRule } from '@casl/ability';
import { SpaceSkuView } from '~/components/space-sku/SpaceSkuView';
import { Spaces } from '~/routes/spaces';
import { DeploymentForm } from './components/assets/DeploymentForm';
import { AuthErrorBoundary } from './components/auth/AuthErrorBoundary';
import { ForgotPassword } from './components/auth/ForgotPassword';
import { Help } from './components/auth/Help';
import { LoginForm } from './components/auth/LoginForm';
import { ReferralRedirect } from './components/auth/ReferralRedirect';
import { ResetPassword } from './components/auth/ResetPassword';
import { SignupForm } from './components/auth/SignupForm';
import { RootBoundary } from './RootBoundary';
import AssetLayout from './routes/assets.$assetId';
import AssetAttachments from './routes/assets.$assetId.attachments';
import {
  default as AssetDeployments,
  loader as assetDeploymentsLoader,
} from './routes/assets.$assetId.deployments';
import * as assets_$assetId_jobs_new from './routes/assets.$assetId.jobs.new';
import * as assets_jobs_new from './routes/assets.jobs.new';

// Contacts
import * as contacts_$contactId from '~/routes/contacts.$contactId';
import * as contacts_$contactId_edit from '~/routes/contacts.$contactId.edit';
import * as contacts__index from '~/routes/contacts._index';
import * as contacts_new from '~/routes/contacts.new';

// Other
import { loadUsetifulScript, setUsetifulTags } from 'usetiful-sdk';
import {
  FORCE_ONBOARDING_FOR_TESTING,
  OnboardingFlowKey,
} from '~/constants/onboarding';
import { AttributeAuditRoute } from './components/job/tasks/AttributeAuditTask';
import { AccountSettings } from './components/users/settings/account';
import { UserSettings } from './components/users/settings/user';
import * as onboarding_chat from './routes/onboarding.chat';

type Session = {
  /** @deprecated Use GraphQL types instead */
  enums: {
    status: ['Active', 'Inactive'];
    assetType: string[];
    organisationType: string[];
  };
  tenant: {
    id: string;
    name: string;
    logo: string | null;
    image: string | null;
    industry: string | null;
    roles: string[];
    /** This is the tenant's contact id for the current user */
    contact_id: string | null;
    /** Tenant timezone in IANA format (e.g., 'Australia/Brisbane') */
    time_zone?: string;
    /** Tenant onboarding fields */
    needsOnboarding?: boolean;
    /** Whether the tenant has completed onboarding */
    onboardingCompleted?: boolean;
    onboardingFlows?: Array<{
      flowKey: string;
      currentStep?: number | null;
      completedAt?: string | null;
    }>;
  };
  user: User | null;
  rules: unknown;
};

type Context = {
  /** @deprecated Use GraphQL types instead */
  enums: Session['enums'];
  tenant: Session['tenant'];
  user: Session['user'];
  ability: Ability;
  isAuthenticated: boolean;
  uppy: UppyInstanceManager;
  revalidate: () => void;
};

/** @deprecated */
export const useEnums = () => {
  const { enums } = useContext(AppContext);

  return enums;
};

export const AppContext = createContext({} as Context);

export const useAppContext = () => useContext(AppContext);

export const useTenant = () => {
  const { tenant } = useAppContext();

  return {
    id: tenant?.id,
  };
};

const Loading = () => <div>Loading...</div>;

async function rootLoader({ request, params }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const token = url.searchParams.get('token');

  // By default all routes are protected unless ignored here
  const ignored = [
    '/login',
    '/signup',
    '/forgot-password',
    '/help',
    '/reset-password',
    '/onboarding/chat',
  ];

  // Allow referral URLs to be accessed without authentication
  if (url.pathname.startsWith('/r/')) {
    ignored.push(url.pathname);
  }
  const isProtected = !ignored.includes(url.pathname);

  const res = await fetch('/api/sessions');

  if (token) {
    // TODO handle this server side
    if (res.status === 403) {
      // Expected current link format is /job/:view where view is actually a job id
      window.location.href = `/in/?job=${params.view}&token=${token}`;
    } else {
      window.location.href = url
        .toString()
        .substring(0, url.toString().indexOf('?'));
    }
    return null;
  }

  if (isProtected) {
    if (res.status === 403) {
      window.sessionStorage.setItem(
        'redirectTo',
        JSON.stringify({
          pathname: `${url.pathname}${getRequestHash(request)}`,
          t: new Date().getTime(),
        })
      );
      return redirect('/login');
    }
    return res;
  }

  // For unprotected routes:
  // - If user is NOT authenticated (403), return null to allow access
  // - If user IS authenticated but on onboarding route, return session to allow access
  // - Otherwise redirect authenticated users to /jobs (or the redirect param if on /login)
  if (res.status === 403) {
    return null;
  }

  if (url.pathname === '/onboarding/chat') {
    return res;
  }

  // If user is logged in and on /login with a redirect param, redirect to that path
  if (url.pathname === '/login') {
    const redirectTo = url.searchParams.get('redirect');
    if (redirectTo) {
      // Validate redirect to prevent open redirect vulnerabilities
      const safeRedirect = validateRedirect(redirectTo, '/jobs');
      return redirect(safeRedirect);
    }
  }

  return redirect('/jobs');
}

const App = () => {
  const data = useLoaderData() as Session[] | null;
  const revalidator = useRevalidator();
  const location = useLocation();

  // Keep a global Uppy instance to manage background uploads
  const uppy = useMemo(() => new UppyInstanceManager(), []);

  const hasSession = (data?.length ?? 0) > 0;
  const isAuthenticated = Boolean(hasSession);
  const session: Session | null = hasSession && data ? data[0] : null;
  const revalidate = () => revalidator.revalidate();

  if (session?.user) {
    const { tenant, user } = session;
    Sentry.setContext('tenant', { id: tenant.id, name: tenant.name });
    Sentry.setUser({ id: user.id, username: user.name, email: user.email });
    // Enable Sentry replays after user login
    Sentry.setTag('replaysSessionSampleRate', '1.0');
    Sentry.setTag('replaysOnErrorSampleRate', '1.0');
  } else {
    // Disable Sentry replays when user is not logged in
    Sentry.setTag('replaysSessionSampleRate', '0.0');
    Sentry.setTag('replaysOnErrorSampleRate', '0.0');
  }

  const ability = new Ability((session?.rules as RawRule[]) || []);

  // Check if tenant needs onboarding
  // Only owners can complete tenant onboarding
  // Don't show onboarding if we're already on the onboarding route
  const isOnOnboardingRoute = location.pathname === '/onboarding/chat';
  const urlParams = new URLSearchParams(location.search);
  const isReplayingOnboarding = urlParams.get('replayOnboarding') === 'true';
  const forceOnboardingForTesting =
    urlParams.get('forceOnboarding') === 'true' || FORCE_ONBOARDING_FOR_TESTING;
  const isOwnerForTenant =
    (session?.tenant?.roles?.includes('owner') ||
      session?.tenant?.roles?.includes('admin')) ??
    false;

  // Determine if the main tenant onboarding flow has already been completed
  const hasCompletedMainTenantFlow =
    session?.tenant?.onboardingFlows?.some(
      (flow) =>
        flow.flowKey === OnboardingFlowKey.TENANT_INITIAL && !!flow.completedAt
    ) ?? false;

  // Check if user has made progress in onboarding (completed at least step 1 - property creation)
  // If they've made progress, don't force them back to onboarding - let them resume via continuation job
  const hasStartedOnboarding =
    session?.tenant?.onboardingFlows?.some(
      (flow) =>
        flow.flowKey === OnboardingFlowKey.TENANT_INITIAL &&
        (flow.currentStep ?? 0) >= 1
    ) ?? false;

  // Check if onboarding was recently completed (grace period to avoid redirect loops)
  // This localStorage flag is set when finishing onboarding and prevents re-redirecting
  // to onboarding if the backend hasn't updated session state yet
  const checkOnboardingJustFinished = () => {
    try {
      const flagData = window.localStorage.getItem('onboardingJustFinished');
      if (!flagData) return false;

      const { timestamp } = JSON.parse(flagData);
      const now = Date.now();
      const gracePeriodMs = 5 * 60 * 1000; // 5 minutes grace period

      // If within grace period, consider onboarding complete
      if (now - timestamp < gracePeriodMs) {
        return true;
      }

      // Grace period expired, clear the flag
      window.localStorage.removeItem('onboardingJustFinished');
      return false;
    } catch {
      return false;
    }
  };

  const onboardingJustFinished = checkOnboardingJustFinished();

  // Also check if tenant has explicit onboardingCompleted boolean (if backend provides it)
  const tenantOnboardingCompleted =
    session?.tenant?.onboardingCompleted ?? false;

  // Clear the grace period flag once server state reflects completion
  if (
    onboardingJustFinished &&
    (hasCompletedMainTenantFlow || tenantOnboardingCompleted)
  ) {
    try {
      window.localStorage.removeItem('onboardingJustFinished');
    } catch {
      // ignore storage errors
    }
  }

  // Determine if user needs to complete onboarding
  // Requirements:
  // 1. User has a tenant
  // 2. Not already on the onboarding route (prevent infinite redirect)
  // 3. Either force flag is set OR (user is owner AND hasn't completed/started main flow AND not in grace period)
  // Note: If user has started onboarding (currentStep >= 1), they can resume via continuation job - don't force redirect
  const needsOnboarding =
    session?.tenant &&
    !isOnOnboardingRoute &&
    !onboardingJustFinished &&
    !tenantOnboardingCompleted &&
    (forceOnboardingForTesting ||
      (isOwnerForTenant &&
        !hasCompletedMainTenantFlow &&
        !hasStartedOnboarding &&
        !isReplayingOnboarding));

  // Set Usetiful user tags when user is authenticated
  // and tenant does not currently need onboarding
  useEffect(() => {
    if (!session?.user) {
      return;
    }

    if (needsOnboarding) {
      return;
    }

    const usetifulKey = import.meta.env.VITE_USETIFUL_KEY;
    if (!usetifulKey) {
      console.warn('VITE_USETIFUL_KEY not configured');
      return;
    }

    const tags: Record<string, string> = {
      userId: session.user.id.toString(),
      firstName: session.user.name?.split(' ')[0] || '',
      lastName: session.user.name?.split(' ').slice(1).join(' ') || '',
    };

    // If onboarding just finished in the previous session, add a one-time tag
    // You can target this in Usetiful to auto-start the post-signup tour
    try {
      const pending = window.localStorage.getItem('postSignupTourPending');
      if (pending) {
        tags.justFinishedOnboarding = '1';
        window.localStorage.removeItem('postSignupTourPending');
      }
    } catch (err) {
      console.error(
        'Failed to read postSignupTourPending from localStorage:',
        err
      );
    }

    // Load the Usetiful script
    loadUsetifulScript(usetifulKey);

    // Wait for Usetiful to be fully ready before setting tags
    // This prevents the "Script is waiting for Progressor data to be ready" error
    const handleUsetifulReady = () => {
      setUsetifulTags(tags);
    };

    // Check if Usetiful is already ready
    if (window.USETIFUL?.reinitialize) {
      // Script is already loaded and ready
      handleUsetifulReady();
    } else {
      // Wait for the script to be ready
      window.addEventListener('usetiful:fullApiReady', handleUsetifulReady, {
        once: true,
      });
    }

    // Cleanup: remove event listener if component unmounts
    return () => {
      window.removeEventListener('usetiful:fullApiReady', handleUsetifulReady);
    };
    // We only care about changes to the current user's identity and onboarding state.
  }, [session?.user, needsOnboarding]);

  // Redirect to onboarding if needed
  if (needsOnboarding) {
    return <Navigate to='/onboarding/chat' />;
  }

  return (
    <Suspense fallback={<Loading />}>
      <SWRConfig value={{ fetcher }}>
        <AppContext.Provider
          value={{
            ...(session ?? ({} as Session)),
            ability,
            isAuthenticated,
            uppy,
            revalidate,
          }}
        >
          <DefaultLayout />
          <ToastContainer autoClose={2000} />
        </AppContext.Provider>
      </SWRConfig>
    </Suspense>
  );
};

const ClientApp = () => (
  <Provider value={client}>
    <App />
  </Provider>
);

function createJobViewRoutes() {
  return (
    <>
      <Route path=':jobId/edit' element={<jobs_$view_$jobId_edit.Component />}>
        <Route
          path='bookings/:bookingId'
          element={<bookings_$bookingId.default />}
        />
      </Route>
      <Route
        path=':jobId'
        loader={jobs_$view_$jobId.loader}
        element={<jobs_$view_$jobId.default />}
      >
        <Route
          path='contacts/:contactId'
          element={<contacts_$contactId.default />}
        />
        <Route
          path='bookings/:bookingId'
          element={<bookings_$bookingId.default />}
        />
        <Route
          path='tasks/:taskId'
          element={<jobs_$view_$jobId_tasks_$taskId.TaskView />}
        />
        <Route path='history' element={<JobHistory />} />
        <Route
          path='new'
          loader={jobs_$view_new.loader}
          element={<jobs_$view_new.default />}
        />
      </Route>
    </>
  );
}

const router = createBrowserRouter([
  {
    id: 'mark-all-as-read',
    path: '/activity/mark-all-as-read',
    action: markAllAsRead,
  },
  {
    id: 'mark-as-read',
    path: '/activity/mark-as-read',
    action: markAsRead,
  },
  // {
  //   id: 'flagged',
  //   path: '/flagged',
  //   action: flagged,
  // },
  ...createRoutesFromElements(
    <Route
      path='/'
      loader={rootLoader}
      element={<ClientApp />}
      errorElement={<RootBoundary />}
    >
      <Route index loader={() => redirect('/jobs')} />
      <Route
        path='login'
        element={<LoginForm />}
        errorElement={<AuthErrorBoundary />}
      />
      <Route
        path='signup'
        element={<SignupForm />}
        errorElement={<AuthErrorBoundary />}
      />
      <Route
        path='r/:referralCode'
        element={<ReferralRedirect />}
        errorElement={<AuthErrorBoundary />}
      />
      <Route
        path='forgot-password'
        element={<ForgotPassword />}
        errorElement={<AuthErrorBoundary />}
      />
      <Route path='help' element={<Help />} />
      <Route path='onboarding/chat' element={<onboarding_chat.default />} />
      <Route
        path='reset-password'
        element={<ResetPassword />}
        errorElement={<AuthErrorBoundary />}
      />
      <Route
        path='accounts/new'
        action={accounts_new.action}
        element={<accounts_new.Component />}
        errorElement={<accounts_new.ErrorBoundary />}
      />
      {/* <Route path='login' loader={() => redirect('/jobs')} />
      <Route path='signup' loader={() => redirect('/jobs')} />
      <Route path='forgot-password' loader={() => redirect('/jobs')} /> */}
      <Route
        id='attribute-audit'
        path='/jobs/:jobId/tasks/:taskId/attribute-audit'
        element={<AttributeAuditRoute />}
      />
      <Route
        id='activity'
        path='activity'
        loader={activity.loader}
        element={<activity.default />}
        shouldRevalidate={activity.shouldRevalidate}
        errorElement={<RootBoundary />}
      >
        <Route path='new' loader={() => redirect('/activity/jobs/new')} />
        <Route
          path='jobs/new'
          loader={jobs_$view_new.loader}
          element={<jobs_$view_new.default />}
        />
        <Route path='jobs'>{createJobViewRoutes()}</Route>
      </Route>
      {/* Deep-link for referral settings - redirects to jobs with org settings popup open */}
      <Route
        path='jobs/settings/referrals'
        element={<Navigate to='/jobs#org-settings-open:referrals' replace />}
      />
      <Route path='jobs' errorElement={<jobs.ErrorBoundary />}>
        <Route index element={<jobs__index.default />} />
        <Route
          path=':view'
          loader={jobs_$view.loader}
          element={<jobs.JobsLayout />}
          id='jobs_$view'
        >
          <Route
            path='new'
            loader={jobs_$view_new.loader}
            element={<jobs_$view_new.default />}
          />
          <Route
            path='calendar/:eventId'
            element={<jobs_$view_calendar_$eventId.Component />}
          />
          {createJobViewRoutes()}
        </Route>
      </Route>
      {/* Monthly calendar at /jobs/calendar and /jobs/calendar/:placeId */}
      <Route
        path='jobs/calendar'
        element={<calendar_monthly_$placeId.default />}
        errorElement={<RootBoundary />}
      />
      <Route
        path='/jobs/calendar/:placeId'
        element={<calendar_monthly_$placeId.default />}
      >
        {/* DayView as child route - inherits monthly calendar context */}
        <Route path='day' element={<DayView />} />
        {/* Bookings detail within monthly context */}
        <Route
          path='bookings/:bookingId'
          element={<bookings_$bookingId.default />}
        />
        <Route path='sites/:siteId' element={<sites_$siteId.default />}>
          {/* Secondary Outlet for Calendar Sites  */}
          {/* Stock */}
          <Route
            path='stock-adjustment'
            element={<sites_$siteId_stocktake.default />}
          />
          <Route
            path='stocktake'
            element={<sites_$siteId_stocktake.default />}
          />
          {/* Jobs */}
          <Route path='jobs'>{createJobViewRoutes()}</Route>
          {/* Items  */}
          <Route path='items/:itemId' element={<SpaceSkuView />} />
          {/* Assets  */}
          <Route path='assets'>
            <Route path='new' element={<sites_$siteId_assets_new.default />} />
            <Route
              path=':assetId'
              element={<sites_$siteId_assets_$assetId.default />}
            >
              <Route
                path='edit'
                element={<sites_$siteId_assets_$assetId_edit.default />}
              />
              <Route
                path='deploy'
                element={<sites_$siteId_assets_$assetId_deploy.default />}
              />
              {/* Jobs for this asset */}
              <Route path='jobs'>
                <Route
                  path='new'
                  loader={sites_$siteId_assets_$assetId_jobs_new.loader}
                  element={<sites_$siteId_assets_$assetId_jobs_new.default />}
                />
                <Route
                  path=':jobId'
                  loader={sites_$siteId_assets_$assetId_jobs_$jobId.loader}
                  element={
                    <sites_$siteId_assets_$assetId_jobs_$jobId.default />
                  }
                >
                  <Route
                    path='edit'
                    element={
                      <sites_$siteId_assets_$assetId_jobs_$jobId_edit.default />
                    }
                  />
                  <Route
                    path='tasks/:taskId'
                    loader={
                      sites_$siteId_assets_$assetId_jobs_$jobId_tasks_$taskId.loader
                    }
                    element={
                      <sites_$siteId_assets_$assetId_jobs_$jobId_tasks_$taskId.default />
                    }
                  />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
        {/* Nested routes for job creation/editing within monthly view */}
        <Route
          path='new'
          loader={jobs_$view_new.loader}
          element={<jobs_$view_new.default />}
        />
        {createJobViewRoutes()}
      </Route>

      {/* Compatibility redirects: support legacy /jobs/calendar/:jobId[(/edit)] deep links */}
      {/* Note: placed AFTER monthly calendar routes to avoid shadowing /jobs/calendar/:placeId (siteId) */}
      <Route
        path='jobs/calendar/:jobId/edit'
        loader={({ params, request }) => {
          const url = new URL(request.url);
          const search = url.search ? url.search : '';
          const hash = url.hash;
          return redirect(`/jobs/planner/${params.jobId}/edit${search}${hash}`);
        }}
      />
      <Route
        path='jobs/calendar/:jobId'
        loader={({ params, request }) => {
          const url = new URL(request.url);
          const search = url.search ? url.search : '';
          const hash = url.hash;
          return redirect(`/jobs/planner/${params.jobId}${search}${hash}`);
        }}
      />

      {/* Planner moved to /jobs/planner. /jobs/calendar becomes monthly root */}
      <Route
        path='jobs/planner'
        element={<calendar__index.default />}
        errorElement={<RootBoundary />}
      >
        {/* DayView as child route - inherits CalendarLayout context */}
        <Route path=':placeId/day' element={<DayView />} />
        {/* Routes rendered in Layout's right-side outlet */}
        <Route
          path='bookings/:bookingId'
          element={<bookings_$bookingId.default />}
        />
        <Route path='sites/:siteId' element={<sites_$siteId.default />}>
          {/* Secondary Outlet for Calendar Sites  */}
          {/* Stock */}
          <Route
            path='stock-adjustment'
            element={<sites_$siteId_stocktake.default />}
          />
          <Route
            path='stocktake'
            element={<sites_$siteId_stocktake.default />}
          />
          {/* Jobs */}
          <Route path='jobs'>{createJobViewRoutes()}</Route>
          {/* Items  */}
          <Route path='items/:itemId' element={<SpaceSkuView />} />
          {/* Assets  */}
          <Route path='assets'>
            <Route path='new' element={<sites_$siteId_assets_new.default />} />
            <Route
              path=':assetId'
              element={<sites_$siteId_assets_$assetId.default />}
            >
              <Route
                path='edit'
                element={<sites_$siteId_assets_$assetId_edit.default />}
              />
              <Route
                path='deploy'
                element={<sites_$siteId_assets_$assetId_deploy.default />}
              />
              {/* Jobs for this asset */}
              <Route path='jobs'>
                <Route
                  path='new'
                  loader={sites_$siteId_assets_$assetId_jobs_new.loader}
                  element={<sites_$siteId_assets_$assetId_jobs_new.default />}
                />
                <Route
                  path=':jobId'
                  loader={sites_$siteId_assets_$assetId_jobs_$jobId.loader}
                  element={
                    <sites_$siteId_assets_$assetId_jobs_$jobId.default />
                  }
                >
                  <Route
                    path='edit'
                    element={
                      <sites_$siteId_assets_$assetId_jobs_$jobId_edit.default />
                    }
                  />
                  <Route
                    path='tasks/:taskId'
                    loader={
                      sites_$siteId_assets_$assetId_jobs_$jobId_tasks_$taskId.loader
                    }
                    element={
                      <sites_$siteId_assets_$assetId_jobs_$jobId_tasks_$taskId.default />
                    }
                  />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
        {/* Calendar Sites Edit */}
        <Route
          path='sites/:siteId/edit'
          loader={sites_$siteId_edit.loader}
          action={sites_$siteId_edit.action}
          element={<sites_$siteId_edit.EditSite />}
        />
        {/* Global job creation and view routes */}
        <Route
          path='new'
          loader={jobs_$view_new.loader}
          element={<jobs_$view_new.default />}
        />
        {createJobViewRoutes()}
      </Route>

      {/* TEMPLATES  */}
      <Route
        path='job-templates'
        loader={templates__index.loader}
        element={<templates__index.default />}
        errorElement={<RootBoundary />}
      >
        <Route
          path='new'
          action={templates_new.action}
          loader={templates_new.loader}
          element={<templates_new.default />}
        />
        <Route path=':templateId' element={<templates_$templateId.default />}>
          <Route path='tasks/:taskId' element={<TemplateTaskView />} />
        </Route>
        <Route
          path=':templateId/edit'
          action={templates_$templateId_edit.action}
          loader={templates_$templateId_edit.loader}
          element={<templates_$templateId_edit.default />}
        />
        <Route
          path=':templateId/clone'
          action={templates_$templateId_clone.action}
          loader={templates_$templateId_clone.loader}
          element={<templates_$templateId_clone.default />}
        />
      </Route>
      <Route
        path='stock-movements'
        element={<StockMovements />}
        errorElement={<RootBoundary />}
      >
        <Route path=':id' element={<StockMovementRoute />} />
      </Route>
      <Route
        path='stock-on-hand/*'
        element={<StockOnHand />}
        errorElement={<RootBoundary />}
      />
      <Route
        path='items/*'
        element={<Items />}
        errorElement={<RootBoundary />}
      />
      <Route
        path='config/*'
        element={<Config />}
        errorElement={<RootBoundary />}
      />
      <Route path='assets' element={<Assets />} errorElement={<RootBoundary />}>
        <Route path='new' element={<AssetForm />} />
        {/* Bulk job creation from assets - must be before :assetId route */}
        <Route
          path='jobs/new'
          loader={assets_jobs_new.loader}
          element={<assets_jobs_new.default />}
        />
        <Route path=':assetId/edit' element={<AssetForm />} />
        <Route path=':assetId/deploy' element={<DeploymentForm />} />
        <Route path=':assetId' element={<AssetLayout />}>
          <Route
            path='deployments'
            loader={assetDeploymentsLoader}
            element={<AssetDeployments />}
          />
          <Route path='attachments' element={<AssetAttachments />} />
          {/* Jobs */}
          <Route path='jobs'>
            <Route
              path='new'
              loader={assets_$assetId_jobs_new.loader}
              element={<assets_$assetId_jobs_new.default />}
            />
            {createJobViewRoutes()}
          </Route>
        </Route>
      </Route>
      {/* <Route path='asset-config/*' element={<Config locationType='Asset' />} /> */}
      <Route
        path='deployments'
        element={<Deployments />}
        errorElement={<RootBoundary />}
      >
        <Route path=':id' element={<DeploymentView />} />
        <Route
          path=':id/edit'
          element={<EditDeploymentForm deployments={[]} />}
        />
      </Route>
      <Route path='asset-models/*' element={<AssetModels />} />
      {/* SITES */}
      <Route
        path='sites'
        element={<sites.default />}
        errorElement={<RootBoundary />}
      >
        {/* Main Outlet for Sites  */}
        <Route
          path='new'
          loader={sites_new.loader}
          action={sites_new.action}
          element={<sites_new.NewSite />}
        />
        <Route
          path=':siteId/edit'
          loader={sites_$siteId_edit.loader}
          action={sites_$siteId_edit.action}
          element={<sites_$siteId_edit.EditSite />}
        />
        <Route path=':siteId' element={<sites_$siteId.default />}>
          {/* Secondary Outlet for Sites  */}
          {/* Stock */}
          <Route
            path='stock-adjustment'
            element={<sites_$siteId_stocktake.default />}
          />
          <Route
            path='stocktake'
            element={<sites_$siteId_stocktake.default />}
          />
          {/* Jobs */}
          <Route path='jobs'>{createJobViewRoutes()}</Route>
          {/* Items  */}
          <Route path='items/:itemId' element={<SpaceSkuView />} />
          {/* Assets  */}
          <Route path='assets'>
            <Route path='new' element={<sites_$siteId_assets_new.default />} />
            <Route
              path=':assetId'
              element={<sites_$siteId_assets_$assetId.default />}
            >
              <Route
                path='edit'
                element={<sites_$siteId_assets_$assetId_edit.default />}
              />
              <Route
                path='deploy'
                element={<sites_$siteId_assets_$assetId_deploy.default />}
              />
              {/* Jobs for this asset */}
              <Route path='jobs'>
                <Route
                  path='new'
                  loader={sites_$siteId_assets_$assetId_jobs_new.loader}
                  element={<sites_$siteId_assets_$assetId_jobs_new.default />}
                />
                <Route
                  path=':jobId'
                  loader={sites_$siteId_assets_$assetId_jobs_$jobId.loader}
                  element={
                    <sites_$siteId_assets_$assetId_jobs_$jobId.default />
                  }
                >
                  <Route
                    path='edit'
                    element={
                      <sites_$siteId_assets_$assetId_jobs_$jobId_edit.default />
                    }
                  />
                  <Route
                    path='tasks/:taskId'
                    loader={
                      sites_$siteId_assets_$assetId_jobs_$jobId_tasks_$taskId.loader
                    }
                    element={
                      <sites_$siteId_assets_$assetId_jobs_$jobId_tasks_$taskId.default />
                    }
                  />
                </Route>
              </Route>
            </Route>
          </Route>
          {/* Spaces  */}
          <Route path='spaces'>
            <Route path='new' element={<sites_$siteId_spaces_new.default />} />
            <Route
              path=':spaceId'
              element={<sites_$siteId_spaces_$spaceId.default />}
            >
              <Route
                path='edit'
                element={<sites_$siteId_spaces_$spaceId_edit.default />}
              />
            </Route>
          </Route>
        </Route>
      </Route>
      <Route
        path='site-config/*'
        element={<Config /* locationType='Site' */ />}
        errorElement={<RootBoundary />}
      />
      <Route
        path='spaces/*'
        element={<Spaces />}
        errorElement={<RootBoundary />}
      />
      <Route
        path='contacts'
        element={<contacts__index.default />}
        errorElement={<RootBoundary />}
      >
        <Route path='new' element={<contacts_new.default />} />
        <Route
          path=':contactId/edit'
          element={<contacts_$contactId_edit.default />}
        />
        <Route path=':contactId' element={<contacts_$contactId.default />} />
      </Route>
      <Route
        path='automations'
        loader={automations__index.loader}
        element={<automations__index.Component />}
        errorElement={<RootBoundary />}
      >
        <Route path='new' element={<automations_new.Component />} />
        <Route
          path=':automationId/edit'
          element={<automations_$automationId_edit.Component />}
        />
        <Route
          path=':automationId'
          element={<automations_$automationId.Component />}
        >
          <Route
            path='tasks/:taskId'
            element={
              <automations_$automationId_tasks_$taskId.AutomationTaskView />
            }
          />
        </Route>
      </Route>
      <Route
        path='organisation-settings'
        element={<AccountSettings />}
        errorElement={<RootBoundary />}
      />
      <Route
        path='user-settings'
        element={<UserSettings />}
        errorElement={<RootBoundary />}
      />
      <Route path='*' element={<NotFound />} />
    </Route>
  ),
]);

const RouterApp = () => <RouterProvider router={router} />;

export { RouterApp as App };
